/**
 * Card Carousel & Guidebook Swipe Support
 *
 * Two features:
 * 1. Marquee Auto-Scroll: Continuous auto-scrolling for .marquee .card-graphics
 *    - Auto-scrolls left continuously
 *    - Pause on hover/touch
 *    - Drag to scroll manually
 *    - Infinite loop effect (seamless, no flash)
 *    - Dark background stays stationary
 *
 * 2. Guidebook Swipe: Touch/drag navigation for .card-collection (Taste of the Deck)
 *    - Swipe left/right to go to next/previous card
 *    - Works with existing arrow buttons (integrates with inline script)
 *    - NO auto-scroll
 */

(function() {
  'use strict';

  // ============================================
  // MARQUEE AUTO-SCROLL (Solution Section)
  // ============================================

  const MARQUEE_CONFIG = {
    autoScrollSpeed: 0.8, // pixels per frame
    dragMultiplier: 1.5,
    momentumDecay: 0.95,
    resumeDelay: 2000
  };

  class MarqueeCarousel {
    constructor(container) {
      this.container = container;
      this.cardGraphics = container.querySelector('.card-graphics');

      if (!this.cardGraphics) {
        console.warn('MarqueeCarousel: No .card-graphics found');
        return;
      }

      this.originalImages = Array.from(this.cardGraphics.querySelectorAll('.card-image'));
      if (this.originalImages.length === 0) return;

      this.isAutoScrolling = true;
      this.isDragging = false;
      this.startX = 0;
      this.scrollLeft = 0;
      this.velocity = 0;
      this.animationId = null;
      this.resumeTimeout = null;
      this.scrollPosition = 0;
      this.trackWidth = 0;

      this.init();
    }

    init() {
      this.setupStructure();
      this.bindEvents();
      this.startAutoScroll();
    }

    setupStructure() {
      // Move background to .marquee, so cards can scroll independently
      const bgColor = window.getComputedStyle(this.cardGraphics).backgroundColor;
      this.container.style.backgroundColor = bgColor;
      this.container.style.overflow = 'hidden';
      this.container.style.position = 'relative';

      // Remove background from card-graphics since it's now on marquee
      this.cardGraphics.style.backgroundColor = 'transparent';
      this.cardGraphics.style.cursor = 'grab';
      this.cardGraphics.style.display = 'flex';
      this.cardGraphics.style.flexWrap = 'nowrap';
      this.cardGraphics.style.width = 'max-content';
      this.cardGraphics.style.transition = 'none';
      this.cardGraphics.style.willChange = 'transform';

      // Clone all images for seamless infinite scroll
      // We clone them once so we have: [original set][cloned set]
      this.originalImages.forEach(img => {
        const clone = img.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.cardGraphics.appendChild(clone);
      });

      // Calculate track width (width of one set of images)
      this.trackWidth = this.cardGraphics.scrollWidth / 2;
    }

    bindEvents() {
      // Mouse events
      this.cardGraphics.addEventListener('mousedown', this.onDragStart.bind(this));
      document.addEventListener('mousemove', this.onDragMove.bind(this));
      document.addEventListener('mouseup', this.onDragEnd.bind(this));

      // Touch events
      this.cardGraphics.addEventListener('touchstart', this.onDragStart.bind(this), { passive: true });
      document.addEventListener('touchmove', this.onDragMove.bind(this), { passive: false });
      document.addEventListener('touchend', this.onDragEnd.bind(this));

      // Hover pause
      this.container.addEventListener('mouseenter', this.pauseAutoScroll.bind(this));
      this.container.addEventListener('mouseleave', this.scheduleResumeAutoScroll.bind(this));

      // Recalculate track width on resize (card sizes change via media queries)
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.trackWidth = this.cardGraphics.scrollWidth / 2;
          this.normalizePosition();
          this.updateTransform();
        }, 200);
      });

      // Prevent context menu during drag
      this.cardGraphics.addEventListener('contextmenu', (e) => {
        if (this.isDragging) e.preventDefault();
      });
    }

    onDragStart(e) {
      this.isDragging = true;
      this.cardGraphics.style.cursor = 'grabbing';

      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      this.startX = clientX;
      this.dragStartPosition = this.scrollPosition;
      this.velocity = 0;
      this.lastClientX = clientX;

      if (this.resumeTimeout) {
        clearTimeout(this.resumeTimeout);
        this.resumeTimeout = null;
      }

      this.pauseAutoScroll();
    }

    onDragMove(e) {
      if (!this.isDragging) return;

      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      const diff = (this.startX - clientX) * MARQUEE_CONFIG.dragMultiplier;

      this.velocity = this.lastClientX - clientX;
      this.lastClientX = clientX;

      this.scrollPosition = this.dragStartPosition + diff;
      this.normalizePosition();
      this.updateTransform();

      if (e.type.includes('touch') && Math.abs(diff) > 5) {
        e.preventDefault();
      }
    }

    onDragEnd() {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.cardGraphics.style.cursor = 'grab';

      this.applyMomentum();
      this.scheduleResumeAutoScroll();
    }

    applyMomentum() {
      if (Math.abs(this.velocity) < 1) return;

      const momentum = () => {
        if (Math.abs(this.velocity) < 0.5 || this.isDragging) {
          this.velocity = 0;
          return;
        }

        this.scrollPosition += this.velocity;
        this.normalizePosition();
        this.updateTransform();
        this.velocity *= MARQUEE_CONFIG.momentumDecay;

        requestAnimationFrame(momentum);
      };

      requestAnimationFrame(momentum);
    }

    normalizePosition() {
      // Keep position within bounds for seamless loop
      // When we scroll past the first set, jump back by one set width
      if (this.scrollPosition >= this.trackWidth) {
        this.scrollPosition -= this.trackWidth;
      } else if (this.scrollPosition < 0) {
        this.scrollPosition += this.trackWidth;
      }
    }

    updateTransform() {
      this.cardGraphics.style.transform = `translateX(-${this.scrollPosition}px)`;
    }

    startAutoScroll() {
      this.isAutoScrolling = true;

      const scroll = () => {
        if (this.isAutoScrolling && !this.isDragging) {
          this.scrollPosition += MARQUEE_CONFIG.autoScrollSpeed;
          this.normalizePosition();
          this.updateTransform();
        }

        this.animationId = requestAnimationFrame(scroll);
      };

      this.animationId = requestAnimationFrame(scroll);
    }

    pauseAutoScroll() {
      this.isAutoScrolling = false;
    }

    scheduleResumeAutoScroll() {
      if (this.resumeTimeout) {
        clearTimeout(this.resumeTimeout);
      }

      this.resumeTimeout = setTimeout(() => {
        if (!this.isDragging) {
          this.isAutoScrolling = true;
        }
      }, MARQUEE_CONFIG.resumeDelay);
    }

    destroy() {
      if (this.animationId) cancelAnimationFrame(this.animationId);
      if (this.resumeTimeout) clearTimeout(this.resumeTimeout);
    }
  }

  // ============================================
  // GUIDEBOOK SWIPE NAVIGATION (Taste of the Deck)
  // Integrates with the existing inline script
  // ============================================

  const SWIPE_CONFIG = {
    threshold: 50, // minimum swipe distance to trigger navigation
    velocityThreshold: 0.3 // minimum velocity to trigger navigation
  };

  class GuidebookSwipe {
    constructor(container) {
      this.container = container;

      // Find the visible next/prev buttons managed by the inline script
      // Instead of duplicating visibility logic, we simply click these buttons
      // so the inline script remains the single source of truth
      this.startX = 0;
      this.startY = 0;
      this.startTime = 0;
      this.isDragging = false;
      this.isHorizontalSwipe = null;

      this.init();
    }

    init() {
      this.bindEvents();
    }

    findVisibleButton(selector) {
      // Find a visible button (the inline script hides non-active cards,
      // so only the current card's buttons are interactive)
      const buttons = this.container.querySelectorAll(selector);
      for (let i = 0; i < buttons.length; i++) {
        const parent = buttons[i].closest('.w-dyn-item');
        if (parent) {
          const style = window.getComputedStyle(parent);
          if (style.visibility !== 'hidden' && style.opacity !== '0' && style.position !== 'absolute') {
            return buttons[i];
          }
        }
        // Fallback: if no w-dyn-item parent, just return first button
        return buttons[i];
      }
      return null;
    }

    bindEvents() {
      // Touch events for mobile swipe
      this.container.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
      this.container.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
      this.container.addEventListener('touchend', this.onTouchEnd.bind(this));

      // Mouse events for desktop drag
      this.container.addEventListener('mousedown', this.onMouseDown.bind(this));
      document.addEventListener('mousemove', this.onMouseMove.bind(this));
      document.addEventListener('mouseup', this.onMouseUp.bind(this));
    }

    onTouchStart(e) {
      this.isDragging = true;
      this.startX = e.touches[0].clientX;
      this.startY = e.touches[0].clientY;
      this.startTime = Date.now();
      this.isHorizontalSwipe = null;
    }

    onTouchMove(e) {
      if (!this.isDragging) return;

      const deltaX = e.touches[0].clientX - this.startX;
      const deltaY = e.touches[0].clientY - this.startY;

      // Determine swipe direction on first significant movement
      if (this.isHorizontalSwipe === null && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
        this.isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
      }

      // Prevent page scroll if horizontal swipe
      if (this.isHorizontalSwipe) {
        e.preventDefault();
      }
    }

    onTouchEnd(e) {
      if (!this.isDragging) return;
      this.isDragging = false;

      const endX = e.changedTouches[0].clientX;
      const deltaX = endX - this.startX;
      const deltaTime = Date.now() - this.startTime;
      const velocity = Math.abs(deltaX) / deltaTime;

      // Only process if it was a horizontal swipe
      if (this.isHorizontalSwipe) {
        if (Math.abs(deltaX) > SWIPE_CONFIG.threshold || velocity > SWIPE_CONFIG.velocityThreshold) {
          if (deltaX > 0) {
            this.goToPrev();
          } else {
            this.goToNext();
          }
        }
      }

      this.isHorizontalSwipe = null;
    }

    onMouseDown(e) {
      // Only on the card area, not buttons
      if (e.target.closest('.previous-button, .next-button')) return;

      this.isDragging = true;
      this.startX = e.clientX;
      this.startTime = Date.now();
      this.container.style.cursor = 'grabbing';
    }

    onMouseMove(e) {
      if (!this.isDragging) return;
    }

    onMouseUp(e) {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.container.style.cursor = '';

      const deltaX = e.clientX - this.startX;
      const deltaTime = Date.now() - this.startTime;
      const velocity = Math.abs(deltaX) / deltaTime;

      if (Math.abs(deltaX) > SWIPE_CONFIG.threshold || velocity > SWIPE_CONFIG.velocityThreshold) {
        if (deltaX > 0) {
          this.goToPrev();
        } else {
          this.goToNext();
        }
      }
    }

    goToNext() {
      // Click the next button on the currently visible card
      // This lets the inline script handle all visibility logic
      const btn = this.findVisibleButton('.next-button');
      if (btn) btn.click();
    }

    goToPrev() {
      // Click the prev button on the currently visible card
      // This lets the inline script handle all visibility logic
      const btn = this.findVisibleButton('.previous-button');
      if (btn) btn.click();
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  function init() {
    // Initialize Marquee Auto-Scroll (solution section card row)
    const marquees = document.querySelectorAll('.marquee');
    marquees.forEach(marquee => {
      if (marquee.dataset.carouselInit) return;
      marquee.dataset.carouselInit = 'true';
      new MarqueeCarousel(marquee);
    });

    // Initialize Guidebook Swipe Navigation (Taste of the Deck)
    // Note: This adds swipe support to the existing button navigation
    const guidebooks = document.querySelectorAll('.card-collection, [udy-collection="cards"]');
    guidebooks.forEach(guidebook => {
      if (guidebook.dataset.swipeInit) return;
      guidebook.dataset.swipeInit = 'true';
      new GuidebookSwipe(guidebook);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose globally
  window.MarqueeCarousel = MarqueeCarousel;
  window.GuidebookSwipe = GuidebookSwipe;
})();
